import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Skeleton } from '@mantine/core'
import { ResponsiveLine, type CustomLayerProps } from '@nivo/line'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import type {
  LinkType,
  PipelineLeadsByPeriodResult,
  PipelinePeriodBucket,
  StatusLogFiltersInput,
} from 'types/graphql'

import { useQuery } from '@redwoodjs/web'

import {
  SegmentedTabs,
  type SegmentedTabItem,
} from 'src/components/Core/SegmentedTabs'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_PIPELINE_LEADS_BY_PERIOD } from 'src/graphql/pipelineMetrics.graphql'
import { LinkTypeDisplay } from 'src/graphql/types/trackingLinks'

import AnalyticsCard from '../../components/AnalyticsCard'

dayjs.extend(utc)

/**
 * Local enum-shaped const so this file doesn't reach into the api package.
 * The string values must match the SDL `PipelinePeriodMetric` enum.
 */
export const Metric = { LEADS: 'LEADS', HIRES: 'HIRES' } as const
export type MetricValue = (typeof Metric)[keyof typeof Metric]

const METRIC_TABS: ReadonlyArray<SegmentedTabItem<MetricValue>> = [
  { value: Metric.LEADS, label: 'Leads' },
  { value: Metric.HIRES, label: 'Hires' },
]

const INFO_TEXT =
  'Lead volume per source over time, with a Total line summing every source shown. The bucket size (day / week / month) adapts to your selected date range — shorter ranges get finer buckets.'

/**
 * Pretty-print a period start date for the column header given the bucket.
 *
 * The backend returns ISO timestamps anchored to UTC midnight (e.g. via
 * `DATE_TRUNC('month', date AT TIME ZONE 'UTC')`). Parsing those with the
 * default local-TZ `dayjs(iso)` constructor in a negative-offset TZ shifts
 * them back a day — so May 1 UTC reads as Apr 30 local and formats as
 * "Apr". Use `dayjs.utc` so the displayed month/day always matches the
 * server-side bucket boundary the user filtered on.
 */
export const formatPeriodLabel = (
  iso: string,
  bucket: PipelinePeriodBucket
): string => {
  const d = dayjs.utc(iso)
  if (bucket === 'DAY') return d.format('M/D')
  if (bucket === 'WEEK') return d.format('M/D')
  return d.format('MMM')
}

/** Series id (and legend / tooltip / table label) of the summed line. */
export const TOTAL_SERIES_ID = 'Total'

// Neutral near-black so Total never reads as one more source.
const TOTAL_COLOR = '#111827'

// d3 `category10` — the scheme this chart used before Total existed. Colors
// are now assigned per series (so Total can opt out), and keeping the same
// hexes in the same order means every source keeps the color it had.
const SOURCE_COLORS = [
  '#1f77b4',
  '#ff7f0e',
  '#2ca02c',
  '#d62728',
  '#9467bd',
  '#8c564b',
  '#e377c2',
  '#7f7f7f',
  '#bcbd22',
  '#17becf',
] as const

export interface LeadsLineSeries {
  id: string
  color: string
  data: { x: string; y: number }[]
}

/** Sum of every source's count for one period; missing cells count as 0. */
const sumPeriod = (
  sources: ReadonlyArray<LinkType>,
  period: string,
  lookup: ReadonlyMap<string, number>
): number =>
  sources.reduce((acc, s) => acc + (lookup.get(`${s}__${period}`) ?? 0), 0)

/**
 * Build the Nivo line series: one per source, plus a Total line that sums
 * every source at each period. Total reads the same `lookup` the per-source
 * lines and the table read, so it always reflects the active filters, date
 * range and lead-source selection — no separate query. Missing
 * source × period cells count as 0, matching how the per-source lines plot.
 *
 * Total is only added when there are 2+ sources: with a single source it
 * would draw exactly on top of that source's line.
 */
export const buildLeadsLineData = (
  sources: ReadonlyArray<LinkType>,
  periods: ReadonlyArray<string>,
  lookup: ReadonlyMap<string, number>,
  bucket: PipelinePeriodBucket
): LeadsLineSeries[] => {
  const sourceSeries = sources.map((source, i) => ({
    id: LinkTypeDisplay[source] ?? source,
    color: SOURCE_COLORS[i % SOURCE_COLORS.length],
    data: periods.map((p) => ({
      x: formatPeriodLabel(p, bucket),
      y: lookup.get(`${source}__${p}`) ?? 0,
    })),
  }))
  if (sources.length < 2) return sourceSeries

  const totalSeries: LeadsLineSeries = {
    id: TOTAL_SERIES_ID,
    color: TOTAL_COLOR,
    data: periods.map((p) => ({
      x: formatPeriodLabel(p, bucket),
      y: sumPeriod(sources, p, lookup),
    })),
  }
  // Total goes last so LinesLayer paints it above the source lines. Nivo
  // lists legend entries in reverse, so it also heads the legend.
  return [...sourceSeries, totalSeries]
}

/**
 * Replaces Nivo's built-in `lines` layer, which has no per-series stroke
 * style, so Total can render dashed and a touch heavier than the sources.
 */
const LinesLayer = ({ series, lineGenerator }: CustomLayerProps) => (
  <g>
    {series.map((serie) => {
      const isTotal = serie.id === TOTAL_SERIES_ID
      return (
        <path
          key={serie.id}
          d={lineGenerator(serie.data.map((d) => d.position)) ?? undefined}
          fill="none"
          stroke={serie.color}
          strokeWidth={isTotal ? 2.5 : 2}
          strokeDasharray={isTotal ? '6 4' : undefined}
        />
      )
    })}
  </g>
)

interface LeadsByPeriodHeatmapViewProps {
  data: PipelineLeadsByPeriodResult | undefined
  metric: MetricValue
  onMetricChange: (m: MetricValue) => void
  loading: boolean
}

/**
 * Presentational implementation. Pure function of `data`, `metric`, and
 * `loading`. The Apollo wrapper below is what's used in production; this
 * split makes the component trivial to render in Storybook + unit tests.
 */
export const LeadsByPeriodHeatmapView = ({
  data,
  metric,
  onMetricChange,
  loading,
}: LeadsByPeriodHeatmapViewProps) => {
  const periods = data?.periods ?? []
  const cells = data?.cells ?? []
  const bucket: PipelinePeriodBucket = data?.bucket ?? 'MONTH'

  // Build source → (periodStart → count) lookup.
  const { sources, lookup } = useMemo(() => {
    const seen = new Set<LinkType>()
    const map = new Map<string, number>()
    for (const c of cells) {
      seen.add(c.source)
      const key = `${c.source}__${c.periodStart}`
      map.set(key, c.count)
    }
    // Order rows by total count desc so the densest source is on top.
    const sourceTotals = new Map<LinkType, number>()
    for (const s of seen) {
      let total = 0
      for (const p of periods) {
        total += map.get(`${s}__${p}`) ?? 0
      }
      sourceTotals.set(s, total)
    }
    const ordered = [...seen].sort(
      (a, b) => (sourceTotals.get(b) ?? 0) - (sourceTotals.get(a) ?? 0)
    )
    return { sources: ordered, lookup: map }
  }, [cells, periods])

  const periodCount = periods.length
  // Title is metric-agnostic per latest UX call: "Pipeline Over Time" reads
  // for both leads and hires (metric is shown via the toggle, not duplicated
  // in the title).
  const title = 'Pipeline Over Time'

  const headerControls = (
    <div className="flex items-center gap-2">
      <SegmentedTabs
        tabs={METRIC_TABS}
        value={metric}
        onChange={onMetricChange}
        ariaLabel="Metric"
      />
    </div>
  )

  // ---- Loading ----
  if (loading && !data) {
    return (
      <AnalyticsCard
        title={title}
        info={INFO_TEXT}
        headerChildren={headerControls}
      >
        <div className="overflow-x-auto" data-testid="heatmap-loading">
          <table className="w-full border-separate border-spacing-1 font-mono text-[11px]">
            <thead>
              <tr>
                <th className="w-[120px]" />
                {Array.from({ length: 6 }).map((_, i) => (
                  <th key={i} className="px-2 py-1.5">
                    <Skeleton height={10} width={36} radius="sm" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, ri) => (
                <tr key={ri}>
                  <th className="pr-2 text-right">
                    <Skeleton height={12} width={70} radius="sm" />
                  </th>
                  {Array.from({ length: 6 }).map((_, ci) => (
                    <td key={ci} className="px-2 py-2">
                      <Skeleton height={28} radius="sm" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AnalyticsCard>
    )
  }

  // ---- Empty ----
  if (cells.length === 0 || periodCount === 0 || sources.length === 0) {
    return (
      <AnalyticsCard
        title={title}
        info={INFO_TEXT}
        headerChildren={headerControls}
      >
        <p
          className="py-2 text-sm text-doubleNickel-gray-500"
          data-testid="heatmap-empty"
        >
          No data for this period
        </p>
      </AnalyticsCard>
    )
  }

  // ---- Loaded ----

  const lineData = buildLeadsLineData(sources, periods, lookup, bucket)
  const showTotal = sources.length > 1

  return (
    <AnalyticsCard
      title={title}
      info={INFO_TEXT}
      headerChildren={headerControls}
    >
      {/* Chart on top — `pb-4` reserves vertical breathing room so the
          slice tooltip (rendered in-chart) doesn't get clipped near the
          bottom of the chart area. */}
      <div
        style={{ height: 360 }}
        className="pb-4"
        data-testid="heatmap-lines-wrap"
      >
        <ResponsiveLine
          data={lineData}
          margin={{ top: 24, right: 130, bottom: 48, left: 56 }}
          xScale={{ type: 'point' }}
          yScale={{ type: 'linear', min: 0, max: 'auto' }}
          axisBottom={{ tickSize: 4, tickPadding: 6 }}
          axisLeft={{
            tickSize: 4,
            tickPadding: 6,
            legend: metric === Metric.HIRES ? 'hires' : 'leads',
            legendOffset: -44,
            legendPosition: 'middle',
          }}
          colors={(serie: LeadsLineSeries) => serie.color}
          layers={[
            'grid',
            'markers',
            'axes',
            'crosshair',
            LinesLayer,
            'points',
            'slices',
            'legends',
          ]}
          pointSize={6}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          // Slice tooltip — vertical x-axis slice that surfaces every
          // source's value at the hovered period in one popover, sorted
          // descending. Default Nivo tooltip ("x: y:") is unhelpful here
          // because the user is comparing sources at a moment in time.
          // Total is pinned above the sources, set off by a divider.
          enableSlices="x"
          sliceTooltip={({ slice }) => {
            const points = slice.points
              .filter((p) => p.serieId !== TOTAL_SERIES_ID)
              .sort(
                (a, b) => Number(b.data.yFormatted) - Number(a.data.yFormatted)
              )
            const totalPoint = slice.points.find(
              (p) => p.serieId === TOTAL_SERIES_ID
            )
            const periodLabel = String(slice.points[0]?.data.x ?? '')
            return (
              <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-[11px] shadow-md">
                <div className="mb-1 text-doubleNickel-gray-500">
                  {periodLabel}
                </div>
                {totalPoint && (
                  <div className="mb-1 flex items-center justify-between gap-3 border-b border-gray-200 pb-1 font-semibold text-doubleNickel-gray-900">
                    <span>{TOTAL_SERIES_ID}</span>
                    <span className="font-mono tabular-nums">
                      {totalPoint.data.yFormatted}
                    </span>
                  </div>
                )}
                <div className="flex flex-col gap-0.5">
                  {points.map((p) => (
                    <div
                      key={p.serieId}
                      className="flex items-center justify-between gap-3"
                    >
                      <span className="flex items-center gap-1.5">
                        <span
                          className="inline-block h-2 w-2 flex-shrink-0 rounded-full"
                          style={{ background: p.serieColor }}
                        />
                        <span className="text-doubleNickel-gray-800">
                          {String(p.serieId)}
                        </span>
                      </span>
                      <span className="font-mono font-semibold tabular-nums text-doubleNickel-gray-900">
                        {p.data.yFormatted}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )
          }}
          legends={[
            {
              anchor: 'right',
              direction: 'column',
              translateX: 120,
              itemWidth: 80,
              itemHeight: 18,
              symbolSize: 10,
            },
          ]}
        />
      </div>

      {/* Table below the chart — same source × period grid the user can
          read row-by-row. */}
      <div className="overflow-x-auto" data-testid="heatmap-table-wrap">
        <table
          className="w-full border-separate border-spacing-1 font-mono text-[11px]"
          data-testid="heatmap-table"
        >
          <thead>
            <tr>
              <th className="w-[130px]" />
              {periods.map((p) => (
                <th
                  key={p}
                  className="px-2 py-1.5 text-center text-[10.5px] font-semibold uppercase tracking-wide text-doubleNickel-gray-500"
                  data-testid="heatmap-col-header"
                >
                  {formatPeriodLabel(p, bucket)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sources.map((source) => {
              const displayName = LinkTypeDisplay[source] ?? source
              return (
                <tr key={source} data-testid="heatmap-row">
                  <th
                    className="pr-2 text-right text-[11px] font-medium text-doubleNickel-gray-700"
                    title={displayName}
                  >
                    {displayName}
                  </th>
                  {periods.map((p) => {
                    const count = lookup.get(`${source}__${p}`) ?? 0
                    return (
                      <td
                        key={p}
                        className={[
                          'rounded text-center text-[11.5px] text-doubleNickel-gray-700',
                          count === 0 ? 'text-doubleNickel-gray-400' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        style={{ padding: '9px 6px', minWidth: 64 }}
                        data-testid="heatmap-cell"
                      >
                        {count === 0 ? '—' : count.toLocaleString()}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
          {showTotal && (
            <tfoot>
              <tr data-testid="heatmap-total-row">
                <th className="pr-2 text-right text-[11px] font-semibold text-doubleNickel-gray-900">
                  {TOTAL_SERIES_ID}
                </th>
                {periods.map((p) => {
                  const total = sumPeriod(sources, p, lookup)
                  return (
                    <td
                      key={p}
                      className={[
                        'rounded border-t border-gray-200 text-center text-[11.5px] font-semibold text-doubleNickel-gray-900',
                        total === 0 ? 'text-doubleNickel-gray-400' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      style={{ padding: '9px 6px', minWidth: 64 }}
                      data-testid="heatmap-total-cell"
                    >
                      {total === 0 ? '—' : total.toLocaleString()}
                    </td>
                  )
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </AnalyticsCard>
  )
}

interface LeadsByPeriodHeatmapProps {
  filters: StatusLogFiltersInput
  /**
   * Bumped by the page-level Refresh button — when this value changes (after
   * the initial mount), the card calls its own `refetch()` to re-pull data
   * with the same filters.
   */
  refreshKey: number
}

/**
 * Apollo-bound wrapper. The metric (Leads / Hires) is local UI state that
 * drives the query input — switching tabs triggers a refetch with the new
 * metric.
 */
const LeadsByPeriodHeatmap = ({
  filters,
  refreshKey,
}: LeadsByPeriodHeatmapProps) => {
  const [metric, setMetric] = useState<MetricValue>(Metric.LEADS)

  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  const { data, loading, refetch } = useQuery(GET_PIPELINE_LEADS_BY_PERIOD, {
    variables: { input: { filters, metric } },
    onError,
    notifyOnNetworkStatusChange: true,
  })

  // Skip the initial mount — the useQuery above already kicked off the first
  // fetch. Only call refetch on subsequent refreshKey changes.
  const isFirstRefreshKey = useRef(true)
  useEffect(() => {
    if (isFirstRefreshKey.current) {
      isFirstRefreshKey.current = false
      return
    }
    refetch()
  }, [refreshKey, refetch])

  return (
    <LeadsByPeriodHeatmapView
      data={data?.pipelineLeadsByPeriod}
      metric={metric}
      onMetricChange={setMetric}
      loading={loading}
    />
  )
}

export default LeadsByPeriodHeatmap
